import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { getSubSpecialities } from '@actions';
import { Form, Alert } from 'antd';
import * as actions from '@actions';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionAction } from '@hooks/useSessionAction';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { SessionForm } from '@components/Clinicians/session-form';
import { useSessionOutcome } from '@hooks/useSessionOutcome';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';

import { SubspecialityFormItem } from './SubspecialityFormItem';
import { SpecialityFormItem } from './SpecialityFormItem';
import AdditionalInformationFormItem from './AdditionalInformationFormItem';
import { ReasonForReferralFormItem, OTHER_OPTION } from './ReasonForReferralFormItem';
import { CallTypeFormItem } from './CallTypeFormItem';
import { AuthSelfBookFormItem } from './AuthSelfBookFormItem';
import { AuthStartDateFormItem } from './AuthStartDateFormItem';
import ActionSubtypeFormItem from './ActionSubtypeFormItem';
import { ReferSelfFormItem } from './ReferSelfFormItem';

import {
  ACTION_BKVCON_CODE,
  ACTION_SUBTYPE_KEY,
  ACTION_SPECIALITY_KEY,
  ACTION_SUBSPECIALITY_KEY,
  ACTION_REASON_KEY,
  ACTION_OTHER_REASON_KEY,
  ACTION_REFER_SELF_KEY,
  ACTION_AUTH_KEY,
  ACTION_AUTH_START_KEY,
  ACTION_CALL_TYPE_KEY,
  ACTION_COMMENT_KEY
} from '../../../session-form';

export const useValidateVirtualConsultationFields = () => {
  const dispatch = useDispatch();
  const form = Form.useFormInstance<SessionForm>();

  const { meta, SUBTYPE_KEY, REFER_SELF_KEY, SPECIALITY_KEY, SUBSPECIALITY_KEY, REASON_KEY } =
    useSessionAction(ACTION_BKVCON_CODE);

  const [referSelf] = useSessionFormState(REFER_SELF_KEY);
  const [clinicianType] = useSessionFormState(SUBTYPE_KEY);
  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [subSpeciality] = useSessionFormState(SUBSPECIALITY_KEY);
  const [reasonForReferral] = useSessionFormState(REASON_KEY);

  const { selectedActions, appointmentVersion } = useSessionOutcome();

  const {
    appointment: { spec_cat_code }
  } = useSessionAppointment();

  const { data: subSpecialitiesMeta } = useQuery({
    queryKey: [`subSpecialitiesMeta${ACTION_BKVCON_CODE}`, spec_cat_code],
    queryFn: () => dispatch(actions.getSubSpecialities(spec_cat_code, 1)),
    select: (data) => {
      return { [spec_cat_code]: data?.areas };
    },
    enabled: !!spec_cat_code,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const hasReasonForReferralOptions = !!meta?.action_reason_codes?.map(
    ({ action_reason_code: value, action_reason_desc: label }) => ({
      value,
      label
    })
  )?.length;

  const referSelfErrorMessage = 'Select refer to self or another clinician';
  const subTypeErrorMessage = 'Select a clinician type';
  const specErrorMessage = 'Select a specialty';
  const subSpecErrorMessage = 'Select a subspecialty';
  const referralReasonErrorMessage = 'Select a reason for referral';

  return useCallback(() => {
    if (appointmentVersion !== 2) return [];
    if (!selectedActions.map(({ appt_action_code }) => appt_action_code).includes(ACTION_BKVCON_CODE)) return [];

    const errors: string[] = [];

    if (referSelf !== 0 && referSelf !== 1) {
      form.setFields([{ name: REFER_SELF_KEY, errors: [referSelfErrorMessage] }]);
      errors.push(referSelfErrorMessage);
    } else {
      form.setFields([{ name: REFER_SELF_KEY, errors: [] }]);
    }

    if (referSelf === 0) {
      if (!clinicianType) {
        form.setFields([{ name: REFER_SELF_KEY, errors: [subTypeErrorMessage] }]);
        errors.push(subTypeErrorMessage);
      } else {
        form.setFields([{ name: REFER_SELF_KEY, errors: [] }]);
      }

      if (clinicianType && !speciality) {
        form.setFields([{ name: SPECIALITY_KEY, errors: [specErrorMessage] }]);
        errors.push(specErrorMessage);
      } else {
        form.setFields([{ name: SPECIALITY_KEY, errors: [] }]);
      }

      if (speciality && subSpecialitiesMeta?.[speciality]?.length && !subSpeciality) {
        form.setFields([{ name: SUBSPECIALITY_KEY, errors: [subSpecErrorMessage] }]);
        errors.push(subSpecErrorMessage);
      } else {
        form.setFields([{ name: SUBSPECIALITY_KEY, errors: [] }]);
      }

      if (!reasonForReferral && hasReasonForReferralOptions) {
        form.setFields([{ name: REASON_KEY, errors: [referralReasonErrorMessage] }]);
        errors.push(referralReasonErrorMessage);
      } else {
        form.setFields([{ name: REASON_KEY, errors: [] }]);
      }
    }

    return errors;
  }, [
    appointmentVersion,
    clinicianType,
    form,
    reasonForReferral,
    referSelf,
    selectedActions,
    speciality,
    subSpecialitiesMeta,
    subSpeciality
  ]);
};

type ReducerActionTypes =
  | 'CLEAR'
  | 'REFER_SELF'
  | 'CLINICIAN_TYPE'
  | 'SPECIALITY'
  | 'SUBSPECIALITY'
  | 'START_DATE'
  | 'SELF_BOOK'
  | 'REFERRAL_REASON'
  | 'ADD_COMMENT'
  | 'CALL_TYPE';

const bookVirtualConsultationReducer = (
  state: SessionForm['appt_action_BKVCON'],
  action: {
    type: ReducerActionTypes;
    value?: string | boolean | 0 | 1;
    clinician_type_code?: string;
    defaultToApptSpecAndSubSpec?: boolean;
    spec_cat_code?: string;
    case_spec_area_code?: string | null;
  }
): SessionForm['appt_action_BKVCON'] => {
  const {
    type,
    value,
    clinician_type_code,
    defaultToApptSpecAndSubSpec = false,
    spec_cat_code,
    case_spec_area_code
  } = action;
  switch (type) {
    case 'CLEAR':
      return {
        appt_action_code: ACTION_BKVCON_CODE,
        refer_self: undefined,
        action_subtype_code: undefined,
        action_spec_cat_code: undefined,
        action_spec_area_code: undefined,
        auth_granted: 0,
        auth_start_date: undefined,
        action_task_comment: undefined,
        action_reason_code: undefined,
        call_type: undefined
      };
    case 'REFER_SELF':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        refer_self: value as 0 | 1,
        action_subtype_code: value ? clinician_type_code : undefined,
        action_spec_cat_code: spec_cat_code,
        action_spec_area_code: !defaultToApptSpecAndSubSpec && case_spec_area_code ? case_spec_area_code : undefined,
        auth_granted: 0
        // auth_start_date not changed
        // action_task_comment not changed
        // action_reason_code not changed
        // call_type not changed
      };
    case 'CLINICIAN_TYPE':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        // refer_self not changed
        action_subtype_code: value as string,
        action_spec_cat_code: spec_cat_code,
        action_spec_area_code: !defaultToApptSpecAndSubSpec && case_spec_area_code ? case_spec_area_code : undefined,
        auth_granted: 0
        // auth_start_date not changed
        // action_task_comment not changed
        // action_reason_code not changed
        // call_type not changed
      };
    case 'SPECIALITY':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        // refer_self not changed
        // action_subtype_code not changed
        action_spec_cat_code: value as string,
        action_spec_area_code: undefined
        // auth_granted not changed
        // auth_start_date not changed
        // action_task_comment not changed
        // action_reason_code not changed
        // call_type not changed
      };
    case 'SUBSPECIALITY':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        // refer_self not changed
        // action_subtype_code not changed
        // action_spec_cat_code not changed
        action_spec_area_code: value as string
        // auth_granted not changed
        // auth_start_date not changed
        // action_task_comment not changed
        // action_reason_code not changed
        // call_type not changed
      };
    case 'START_DATE':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        // refer_self not changed
        // action_subtype_code not changed
        // action_spec_cat_code not changed
        // action_spec_area_code not changed
        // auth_granted not changed
        auth_start_date: (value as string) || undefined
        // action_task_comment not changed
        // action_reason_code not changed
        // call_type not changed
      };
    case 'SELF_BOOK':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        // refer_self not changed
        // action_subtype_code not changed
        // action_spec_cat_code not changed
        // action_spec_area_code not changed
        auth_granted: value ? 1 : 0,
        // auth_start_date not changed
        action_task_comment: value ? undefined : state?.action_task_comment,
        // action_reason_code not changed
        call_type: undefined
      };
    case 'REFERRAL_REASON':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        // refer_self not changed
        // action_subtype_code not changed
        // action_spec_cat_code not changed
        // action_spec_area_code not changed
        // auth_granted not changed
        // auth_start_date not changed
        // action_task_comment not changed
        action_reason_code: value as string
        // call_type not changed
      };
    case 'ADD_COMMENT':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        // refer_self not changed
        // action_subtype_code not changed
        // action_spec_cat_code not changed
        // action_spec_area_code not changed
        // auth_granted not changed
        // auth_start_date not changed
        action_task_comment: (value as string) || undefined
        // action_reason_code not changed
        // call_type not changed
      };
    case 'CALL_TYPE':
      return {
        ...state,
        appt_action_code: ACTION_BKVCON_CODE,
        // appt_action_code not changed
        // refer_self not changed
        // action_subtype_code not changed
        // action_spec_cat_code not changed
        // action_spec_area_code not changed
        // auth_granted not changed
        // auth_start_date not changed
        // action_task_comment not changed
        // action_reason_code not changed
        call_type: (value as 'AUDIO' | 'VIDEO') || undefined
      };
    default:
      return state;
  }
};

const BKVCON_REFER_SELF_KEY = `${ACTION_REFER_SELF_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_SUBTYPE_KEY = `${ACTION_SUBTYPE_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_SPECIALITY_KEY = `${ACTION_SPECIALITY_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_SUBSPECIALITY_KEY = `${ACTION_SUBSPECIALITY_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_REASON_KEY = `${ACTION_REASON_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_OTHER_REASON_KEY = `${ACTION_OTHER_REASON_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_AUTH_KEY = `${ACTION_AUTH_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_AUTH_START_KEY = `${ACTION_AUTH_START_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_CALL_TYPE_KEY = `${ACTION_CALL_TYPE_KEY}_${ACTION_BKVCON_CODE}`;
const BKVCON_COMMENT_KEY = `${ACTION_COMMENT_KEY}_${ACTION_BKVCON_CODE}`;

type InputRequirement = 'required' | 'optional' | 'disabled';

type VirtualConsultationFieldsetSessionFormKeys = Extract<
  keyof SessionForm,
  | typeof BKVCON_REFER_SELF_KEY
  | typeof BKVCON_SUBTYPE_KEY
  | typeof BKVCON_SPECIALITY_KEY
  | typeof BKVCON_SUBSPECIALITY_KEY
  | typeof BKVCON_REASON_KEY
  | typeof BKVCON_OTHER_REASON_KEY
  | typeof BKVCON_AUTH_KEY
  | typeof BKVCON_AUTH_START_KEY
  | typeof BKVCON_CALL_TYPE_KEY
  | typeof BKVCON_COMMENT_KEY
>;

const useVirtualConsultationFieldsetRequirements = (): Record<
  VirtualConsultationFieldsetSessionFormKeys,
  InputRequirement
> => {
  const dispatch = useDispatch();
  const { data: appointmentMeta } = useAppointmentMeta();

  const {
    meta,
    selectedSubType: clinicianType,
    SUBTYPE_KEY,
    SPECIALITY_KEY,
    REASON_KEY,
    CALL_TYPE_KEY,
    SUBSPECIALITY_KEY,
    COMMENT_KEY,
    OTHER_REASON_KEY,
    REFER_SELF_KEY,
    AUTH_KEY,
    AUTH_START_KEY
  } = useSessionAction(ACTION_BKVCON_CODE);

  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [auth] = useSessionFormState(AUTH_KEY);
  const [referSelf] = useSessionFormState(REFER_SELF_KEY);
  const [reasonCode] = useSessionFormState(REASON_KEY);

  const showAuthCheckbox = useMemo(() => {
    if (!clinicianType) return false;
    const action_subtype = meta?.action_subtypes?.find(
      ({ action_subtype_code }) => action_subtype_code === clinicianType
    );

    return action_subtype?.auth_enabled;
  }, [meta?.action_subtypes, clinicianType]);

  const { data: requiresSubspeciality } = useQuery({
    queryKey: [`subSpecialitiesMeta${ACTION_BKVCON_CODE}`, speciality],
    queryFn: () => dispatch(getSubSpecialities(String(speciality), 1)),
    select: (data) => {
      if (!speciality) return false;
      const subSpecs = data?.areas ?? (data as any)?.[speciality];
      return Number((subSpecs || []).length) > 0;
    },
    enabled: !!speciality,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const callTypeOptions = useMemo(() => {
    return (
      appointmentMeta?.call_types?.map((type) => ({
        value: type.call_type_code,
        label: type.call_type_desc
      })) || []
    );
  }, [appointmentMeta]);

  const reasonForReferralOptions = useMemo(() => {
    return (
      meta?.action_reason_codes?.map(({ action_reason_code: value, action_reason_desc: label }) => ({
        value,
        label
      })) || []
    );
  }, [meta?.action_reason_codes]);

  const reasonRequiredOrOptional = !referSelf ? 'required' : 'optional';

  return {
    [REFER_SELF_KEY]: 'required',
    [SUBTYPE_KEY]: referSelf === 0 ? 'required' : 'disabled',
    [SPECIALITY_KEY]: referSelf === 0 ? 'required' : 'disabled',
    [SUBSPECIALITY_KEY]: referSelf === 0 && requiresSubspeciality && !!speciality ? 'required' : 'disabled',
    [AUTH_START_KEY]: 'optional',
    [AUTH_KEY]: showAuthCheckbox ? 'optional' : 'disabled',
    [CALL_TYPE_KEY]: !auth && callTypeOptions?.length > 0 ? 'required' : 'disabled',
    [REASON_KEY]: reasonForReferralOptions.length !== 0 ? reasonRequiredOrOptional : 'disabled',
    [OTHER_REASON_KEY]: reasonCode === OTHER_OPTION.value ? 'required' : 'disabled',
    [COMMENT_KEY]: !showAuthCheckbox || (showAuthCheckbox && !auth) ? 'optional' : 'disabled'
  };
};

const useInitialiseVirtualConsultationFieldset = () => {
  const dispatch = useDispatch();
  const appointment_id = useSessionAppointmentId();
  const {
    appointment: { spec_cat_code, case_spec_area_code }
  } = useSessionAppointment();

  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);
  // Support the ability to change subspecialty if a default subspecialty was selected before
  const defaultToApptSpecAndSubSpec = !!detailsCurrentAppointment?.general_subspecialty;
  const savedVirtualConsultationAction = useMemo(
    () =>
      detailsCurrentAppointment?.appt_actions.find(({ appt_action_code }) => appt_action_code === ACTION_BKVCON_CODE),
    [detailsCurrentAppointment?.appt_actions]
  );

  const comment = detailsCurrentAppointment?.appt_actions?.find(
    ({ appt_action_code }) => appt_action_code === ACTION_BKVCON_CODE
  )?.action_task_comment;

  const {
    SUBTYPE_KEY,
    AUTH_KEY,
    AUTH_START_KEY,
    COMMENT_KEY,
    CALL_TYPE_KEY,
    REFER_SELF_KEY,
    SPECIALITY_KEY,
    SUBSPECIALITY_KEY,
    REASON_KEY
  } = useSessionAction(ACTION_BKVCON_CODE);

  const { data: subSpecialitiesMeta } = useQuery({
    queryKey: [`subSpecialitiesMeta${ACTION_BKVCON_CODE}`, spec_cat_code],
    queryFn: () => dispatch(actions.getSubSpecialities(spec_cat_code, 1)),
    select: (data) => {
      return { [spec_cat_code]: data?.areas };
    },
    enabled: !!spec_cat_code,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const validateSubSpeciality = (areaCode: string | undefined | null) => {
    if (!areaCode) return undefined;

    if (!subSpecialitiesMeta) return undefined;

    const areas = subSpecialitiesMeta[spec_cat_code];
    if (!areas) return areaCode;

    const exists = areas.some((area) => area.area_code === areaCode);
    return exists ? areaCode : undefined;
  };

  useSessionFormState(
    REFER_SELF_KEY,
    typeof savedVirtualConsultationAction?.refer_self === 'number'
      ? (savedVirtualConsultationAction?.refer_self as 0 | 1)
      : undefined
  );
  useSessionFormState(SUBTYPE_KEY, savedVirtualConsultationAction?.action_subtype_code || undefined);
  useSessionFormState(SPECIALITY_KEY, savedVirtualConsultationAction?.action_spec_cat_code || spec_cat_code);

  useSessionFormState(
    SUBSPECIALITY_KEY,
    defaultToApptSpecAndSubSpec
      ? validateSubSpeciality(savedVirtualConsultationAction?.action_spec_area_code)
      : (case_spec_area_code ?? undefined)
  );

  useSessionFormState(
    AUTH_START_KEY,
    savedVirtualConsultationAction?.auth_start_date
      ? new Date(savedVirtualConsultationAction?.auth_start_date)
      : undefined
  );
  useSessionFormState(AUTH_KEY, !!savedVirtualConsultationAction?.auth_granted);
  const [, setActionTaskComment] = useSessionFormState(
    COMMENT_KEY,
    savedVirtualConsultationAction?.action_task_comment || undefined
  );
  useSessionFormState(REASON_KEY, savedVirtualConsultationAction?.action_reason_code || undefined);
  useSessionFormState(CALL_TYPE_KEY, savedVirtualConsultationAction?.call_type || undefined);

  useEffect(() => {
    if (comment) setActionTaskComment(comment || undefined);
  }, [comment, setActionTaskComment]);
};

export const BookVirtualConsultationFields: React.FC = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const appointment_id = useSessionAppointmentId();

  const form = Form.useFormInstance<SessionForm>();

  const {
    appointment: {
      appointment_completed: isCompleted,
      read_only: readonly,
      clinician_type_code,
      spec_cat_code,
      case_spec_area_code
    }
  } = useSessionAppointment();

  const { detailsCurrentAppointment } = useAppointmentDetails(appointment_id);
  // Support the ability to change subspecialty if a default subspecialty was selected before
  const defaultToApptSpecAndSubSpec = !!detailsCurrentAppointment?.general_subspecialty;

  const savedVirtualConsultationAction = useMemo(
    () =>
      detailsCurrentAppointment?.appt_actions.find(({ appt_action_code }) => appt_action_code === ACTION_BKVCON_CODE),
    [detailsCurrentAppointment?.appt_actions]
  );

  const {
    meta,
    ACTION_KEY,
    SUBTYPE_KEY,
    AUTH_KEY,
    COMMENT_KEY,
    CALL_TYPE_KEY,
    REFER_SELF_KEY,
    SPECIALITY_KEY,
    SUBSPECIALITY_KEY,
    REASON_KEY
  } = useSessionAction(ACTION_BKVCON_CODE);

  useInitialiseVirtualConsultationFieldset();

  const inputRequirements = useVirtualConsultationFieldsetRequirements();

  const [, setReferSelf] = useSessionFormState(REFER_SELF_KEY);
  const [clinicianType, setClinicianType] = useSessionFormState(SUBTYPE_KEY);

  const taskEnabled = useMemo(() => {
    if (!clinicianType) {
      return !!meta?.action_subtypes?.[0]?.task_enabled;
    }

    const action_subtype = meta?.action_subtypes?.find(
      ({ action_subtype_code }) => action_subtype_code === clinicianType
    );
    return !!action_subtype?.task_enabled;
  }, [meta?.action_subtypes, clinicianType]);

  const [speciality] = useSessionFormState(SPECIALITY_KEY);
  const [, setSubSpeciality] = useSessionFormState(SUBSPECIALITY_KEY);

  const [, setCallType] = useSessionFormState(CALL_TYPE_KEY);
  const [auth, setAuth] = useSessionFormState(AUTH_KEY);
  const [, setActionTaskComment] = useSessionFormState(COMMENT_KEY);
  const [, setVirtualConsultationAction] = useSessionFormState(ACTION_KEY);

  const [vconActionState, vconActionDispatch] = useReducer(
    bookVirtualConsultationReducer,
    savedVirtualConsultationAction as SessionForm[typeof ACTION_KEY]
  );

  useEffect(() => {
    setVirtualConsultationAction(vconActionState);
  }, [vconActionState]);

  // Values passed into reduce dispatch call everytime
  const reducerDispatchData = {
    clinician_type_code,
    defaultToApptSpecAndSubSpec,
    spec_cat_code,
    case_spec_area_code
  };

  useEffect(() => {
    // if saved details changes, and BKVCON action not selected, reset all form fields
    if (detailsCurrentAppointment?.appt_actions && !savedVirtualConsultationAction) {
      // Individual form items now cleared automatically by useClearSessionForm
      vconActionDispatch({
        type: 'CLEAR'
      });
    }
  }, [detailsCurrentAppointment?.appt_actions, savedVirtualConsultationAction]);

  const reasonForReferralOptions = useMemo(() => {
    return (
      meta?.action_reason_codes?.map(({ action_reason_code: value, action_reason_desc: label }) => ({
        value,
        label
      })) || []
    );
  }, [meta?.action_reason_codes]);

  const showAuthCheckbox = useMemo(() => {
    if (!clinicianType) return false;
    const action_subtype = meta?.action_subtypes?.find(
      ({ action_subtype_code }) => action_subtype_code === clinicianType
    );

    return action_subtype?.auth_enabled;
  }, [meta?.action_subtypes, clinicianType]);

  const { data: subSpecialitiesMeta } = useQuery({
    queryKey: [`subSpecialitiesMeta${ACTION_BKVCON_CODE}`, spec_cat_code],
    queryFn: () => dispatch(actions.getSubSpecialities(spec_cat_code, 1)),
    select: (data) => {
      return { [spec_cat_code]: data?.areas };
    },
    enabled: !!spec_cat_code,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  // 1 = refer to self
  // 0 = refer to another specialist
  const handleReferralChange = (value: 0 | 1) => {
    setReferSelf(value);

    if (value === 1) {
      form.setFields([{ name: REASON_KEY, errors: [] }]);
    }

    // If referring to self, then populate with clinician's type
    setClinicianType(value ? clinician_type_code : undefined);
    setSubSpeciality(!defaultToApptSpecAndSubSpec && case_spec_area_code ? case_spec_area_code : undefined);

    setAuth(false);

    vconActionDispatch({
      type: 'REFER_SELF',
      value,
      ...reducerDispatchData
    });
  };

  const handleClinicianTypeChange = (value: string) => {
    setClinicianType(value);
    setSubSpeciality(!defaultToApptSpecAndSubSpec && case_spec_area_code ? case_spec_area_code : undefined);

    setAuth(false);

    vconActionDispatch({
      type: 'CLINICIAN_TYPE',
      value,
      ...reducerDispatchData
    });
  };

  return (
    <>
      <ReferSelfFormItem actionCode={ACTION_BKVCON_CODE} onChange={handleReferralChange} />
      {inputRequirements[SUBTYPE_KEY] !== 'disabled' && (
        <ActionSubtypeFormItem actionCode={ACTION_BKVCON_CODE} onChange={handleClinicianTypeChange} />
      )}

      <>
        {inputRequirements[SPECIALITY_KEY] !== 'disabled' && (
          <SpecialityFormItem
            actionCode={ACTION_BKVCON_CODE}
            disabled
            onChange={(value) => {
              vconActionDispatch({
                type: 'SPECIALITY',
                value,
                ...reducerDispatchData
              });
            }}
          />
        )}

        {inputRequirements[SUBSPECIALITY_KEY] !== 'disabled' && (
          <SubspecialityFormItem
            actionCode={ACTION_BKVCON_CODE}
            disabled={
              !clinicianType || !subSpecialitiesMeta?.[speciality || '']?.length || !defaultToApptSpecAndSubSpec
            }
            onChange={(value) => {
              vconActionDispatch({
                type: 'SUBSPECIALITY',
                value,
                ...reducerDispatchData
              });
            }}
          />
        )}
      </>

      <AuthStartDateFormItem
        actionCode={ACTION_BKVCON_CODE}
        onChange={(value) => {
          vconActionDispatch({
            type: 'START_DATE',
            value: value?.format('YYYY-MM-DD') || undefined,
            ...reducerDispatchData
          });
        }}
      />

      {inputRequirements[AUTH_KEY] !== 'disabled' && (
        <AuthSelfBookFormItem
          actionCode={ACTION_BKVCON_CODE}
          onChange={(e) => {
            if (e.target.checked) {
              setActionTaskComment(undefined);
              setCallType(undefined);
            }
            vconActionDispatch({
              type: 'SELF_BOOK',
              value: e.target.checked ? 'true' : undefined,
              ...reducerDispatchData
            });
          }}
        />
      )}

      {inputRequirements[CALL_TYPE_KEY] !== 'disabled' && (
        <CallTypeFormItem
          actionCode={ACTION_BKVCON_CODE}
          onChange={(value) => {
            vconActionDispatch({
              type: 'CALL_TYPE',
              value,
              ...reducerDispatchData
            });
          }}
        />
      )}

      {inputRequirements[REASON_KEY] !== 'disabled' && (
        <ReasonForReferralFormItem
          actionCode={ACTION_BKVCON_CODE}
          referralOptions={reasonForReferralOptions}
          required={inputRequirements[REASON_KEY] === 'required'}
          onChange={(value) => {
            vconActionDispatch({
              type: 'REFERRAL_REASON',
              value,
              ...reducerDispatchData
            });
          }}
        />
      )}

      {inputRequirements[COMMENT_KEY] !== 'disabled' && (
        <AdditionalInformationFormItem
          actionCode={ACTION_BKVCON_CODE}
          onChange={(value) => {
            vconActionDispatch({
              type: 'ADD_COMMENT',
              value,
              ...reducerDispatchData
            });
          }}
        />
      )}

      {!!showAuthCheckbox && (auth || taskEnabled) && (
        <Alert
          message={
            auth ? (
              <>{t('The patient will be sent an email and allowed to book their own appointment.')}</>
            ) : (
              <>
                {t('If self-book is ')}
                <b>{t('NOT')}</b> {t(' selected, a task will be raised once appointment is complete.')}
              </>
            )
          }
          type="info"
          showIcon
        />
      )}
      {!showAuthCheckbox && !isCompleted && !readonly && taskEnabled && (
        <Alert message={t('A task will be raised once appointment is complete.')} type="info" showIcon />
      )}
    </>
  );
};
